import React from "react";
import PropTypes from "prop-types";

const GENERIC_MESSAGE = "Something went wrong saving your changes. Please try again.";

// The server (Id::Api::ApiController#render_validation_errors) returns
// { errors: [{ detail, source, status, title }] } with `source` a plain attribute name.
// Two error shapes legitimately reach here: the profile edit hooks store the THROWN ApiMutationError
// (structured errors live at generalError.body.errors), while the company edit forms store the raw
// non-thrown response (errors at generalError.error.body.errors). Both ship today, so tolerate both
// rather than force a hook refactor.
function extractErrors(generalError) {
  return generalError?.body?.errors ?? generalError?.error?.body?.errors ?? null;
}

function humanize(source) {
  const words = String(source).replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function lineFor(error, labelMap) {
  const label = labelMap[error.source] ?? humanize(error.source);
  // Never leak a database column name to the user: the server phrases the cross-field date rule
  // against the `ended_at` column, but the form calls it the end date.
  const detail = error.detail === "must be before the ended_at date" ? "must be before the end date" : error.detail;
  return `${label} ${detail}`;
}

/**
 * ServerErrorAlert — renders a server-side save failure inside the shared `alert alert-danger` chrome.
 *
 * When the failure carries structured 422 field errors it lists each as "<Label> <detail>"
 * (e.g. "Start date must be in the past"); otherwise (network failure, 500, missing body) it falls
 * back to the generic copy so existing behaviour/tests stay stable. Renders nothing when there is no
 * error.
 *
 * @param {Object} props
 * @param {*} props.generalError - The stored save error (a thrown ApiMutationError or a raw response).
 * @param {Object} [props.labelMap] - Maps a field `source` to its on-screen label.
 * @param {string} [props.genericMessage] - Overrides the fallback copy for unstructured failures.
 */
function ServerErrorAlert({ generalError, labelMap = {}, genericMessage = GENERIC_MESSAGE }) {
  if (!generalError) return null;

  const errors = extractErrors(generalError);

  return (
    <div className="alert alert-danger" role="alert">
      {errors && errors.length > 0
        ? errors.map((error) => <div key={`${error.source}-${error.detail}`}>{lineFor(error, labelMap)}</div>)
        : genericMessage}
    </div>
  );
}

ServerErrorAlert.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  generalError: PropTypes.any,
  // eslint-disable-next-line react/forbid-prop-types
  labelMap: PropTypes.object,
  genericMessage: PropTypes.string,
};

export default ServerErrorAlert;
